/**
 * Lightweight interaction + alert sounds, synthesised with the Web Audio API.
 *
 * Why generated tones instead of audio files: they need no binary assets, load
 * instantly, work identically in the Capacitor WebView and the PWA, and give a
 * distinct, recognisable cue per event ("that's an alert" vs "that worked").
 *
 * Respects a per-device mute preference and the browser autoplay policy — the
 * AudioContext is created lazily on the first real user gesture, so nothing
 * throws on a page that never makes noise.
 */
import { safeStorage } from './safe-storage'

const MUTE_KEY = 'sound_muted'

let ctx: AudioContext | null = null

function audioContext (): AudioContext | null {
  if (typeof window === 'undefined') return null
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      ctx = new Ctor()
    }
    // Autoplay policy can leave it suspended until a gesture resumes it.
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

export function isMuted (): boolean {
  return safeStorage.get(MUTE_KEY) === 'true'
}

export function setMuted (muted: boolean): void {
  safeStorage.set(MUTE_KEY, muted ? 'true' : 'false')
}

interface ToneStep {
  freq: number
  /** seconds */
  dur: number
  type?: OscillatorType
  /** 0..1 peak gain */
  gain?: number
}

/** Play a sequence of short tones. Fully guarded — never throws to the caller. */
function playSequence (steps: ToneStep[]): void {
  if (isMuted()) return
  const ac = audioContext()
  if (!ac) return
  try {
    let t = ac.currentTime
    for (const step of steps) {
      const osc = ac.createOscillator()
      const gain = ac.createGain()
      osc.type = step.type ?? 'sine'
      osc.frequency.setValueAtTime(step.freq, t)

      const peak = step.gain ?? 0.14
      // Quick attack + exponential release keeps it a soft "blip", not a beep.
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(peak, t + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + step.dur)

      osc.connect(gain)
      gain.connect(ac.destination)
      osc.start(t)
      osc.stop(t + step.dur + 0.02)
      t += step.dur
    }
  } catch {
    /* audio not available — silent no-op */
  }
}

/** A soft tick for taps / selections. */
export const playTap = () => playSequence([{ freq: 440, dur: 0.05, type: 'triangle', gain: 0.08 }])

/** Rising two-note "done" chime for successful actions (submit, save). */
export const playSuccess = () => playSequence([
  { freq: 660, dur: 0.09, type: 'sine' },
  { freq: 990, dur: 0.16, type: 'sine' }
])

/** Low buzz for errors / failed actions. */
export const playError = () => playSequence([
  { freq: 220, dur: 0.16, type: 'sawtooth', gain: 0.12 },
  { freq: 180, dur: 0.20, type: 'sawtooth', gain: 0.12 }
])

/** Bright blip confirming a QR/scan capture. */
export const playScan = () => playSequence([
  { freq: 880, dur: 0.06, type: 'square', gain: 0.10 },
  { freq: 1320, dur: 0.10, type: 'square', gain: 0.10 }
])

/** Urgent three-note alert — for an incoming alert/notification. */
export const playAlert = () => playSequence([
  { freq: 880, dur: 0.12, type: 'triangle', gain: 0.16 },
  { freq: 660, dur: 0.12, type: 'triangle', gain: 0.16 },
  { freq: 990, dur: 0.20, type: 'triangle', gain: 0.16 }
])

/** Distinct, more insistent cue for a missed-cycle alert. */
export const playMissedAlert = () => playSequence([
  { freq: 520, dur: 0.14, type: 'sawtooth', gain: 0.15 },
  { freq: 415, dur: 0.14, type: 'sawtooth', gain: 0.15 },
  { freq: 330, dur: 0.24, type: 'sawtooth', gain: 0.15 }
])

/** Map a notification/alert type to its sound. */
export function playForNotificationType (type?: string): void {
  const t = (type || '').toLowerCase()
  if (t.includes('missed')) return playMissedAlert()
  return playAlert()
}
